import { createSlice } from "@reduxjs/toolkit";
import {
  fetchNephrologyExam,
  fetchNephrologyHistory,
  fetchNephrologyUrinaryAndUremia,
  saveNephrologyExam,
  saveNephrologyHistory,
} from "../thunks/nephrologyHistoryThunks";

const initialState = {
  historyByPatientId: {},
  fetchStatusByPatientId: {},
  fetchErrorByPatientId: {},
  saveStatusByPatientId: {},
  saveErrorByPatientId: {},
  examByPatientId: {},
  examFetchStatusByPatientId: {},
  examFetchErrorByPatientId: {},
  examSaveStatusByPatientId: {},
  examSaveErrorByPatientId: {},
  urinaryAndUremiaByPatientId: {},
  urinaryAndUremiaFetchStatusByPatientId: {},
  urinaryAndUremiaFetchErrorByPatientId: {},
};

const nephrologySlice = createSlice({
  name: "nephrology",
  initialState,
  reducers: {
    clearNephrologyHistory(state, action) {
      const patientId = String(action.payload || "");
      delete state.historyByPatientId[patientId];
      delete state.fetchStatusByPatientId[patientId];
      delete state.fetchErrorByPatientId[patientId];
      delete state.saveStatusByPatientId[patientId];
      delete state.saveErrorByPatientId[patientId];
      delete state.examByPatientId[patientId];
      delete state.examFetchStatusByPatientId[patientId];
      delete state.examFetchErrorByPatientId[patientId];
      delete state.examSaveStatusByPatientId[patientId];
      delete state.examSaveErrorByPatientId[patientId];
      delete state.urinaryAndUremiaByPatientId[patientId];
      delete state.urinaryAndUremiaFetchStatusByPatientId[patientId];
      delete state.urinaryAndUremiaFetchErrorByPatientId[patientId];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNephrologyHistory.pending, (state, action) => {
        const patientId = String(action.meta.arg || "");
        state.fetchStatusByPatientId[patientId] = "loading";
        state.fetchErrorByPatientId[patientId] = null;
      })
      .addCase(fetchNephrologyHistory.fulfilled, (state, action) => {
        const { patientId, records } = action.payload;
        state.historyByPatientId[patientId] = records;
        state.fetchStatusByPatientId[patientId] = "succeeded";
        state.fetchErrorByPatientId[patientId] = null;
      })
      .addCase(fetchNephrologyHistory.rejected, (state, action) => {
        const patientId = String(action.meta.arg || "");
        state.fetchStatusByPatientId[patientId] = "failed";
        state.fetchErrorByPatientId[patientId] = action.payload || action.error?.message;
      })
      .addCase(saveNephrologyHistory.pending, (state, action) => {
        const patientId = String(action.meta.arg?.patientId || "");
        state.saveStatusByPatientId[patientId] = "saving";
        state.saveErrorByPatientId[patientId] = null;
      })
      .addCase(saveNephrologyHistory.fulfilled, (state, action) => {
        const { patientId, record } = action.payload;
        const records = state.historyByPatientId[patientId] || [];
        const index = records.findIndex((item) => item.id === record?.id);
        if (index < 0) records.unshift(record);
        else records[index] = record;
        state.historyByPatientId[patientId] = records;
        state.saveStatusByPatientId[patientId] = "succeeded";
        state.saveErrorByPatientId[patientId] = null;
      })
      .addCase(saveNephrologyHistory.rejected, (state, action) => {
        const patientId = String(action.meta.arg?.patientId || "");
        state.saveStatusByPatientId[patientId] = "failed";
        state.saveErrorByPatientId[patientId] = action.payload || action.error?.message;
      })
      .addCase(fetchNephrologyExam.pending, (state, action) => {
        const patientId = String(action.meta.arg || "");
        state.examFetchStatusByPatientId[patientId] = "loading";
        state.examFetchErrorByPatientId[patientId] = null;
      })
      .addCase(fetchNephrologyExam.fulfilled, (state, action) => {
        const { patientId, records } = action.payload;
        state.examByPatientId[patientId] = records;
        state.examFetchStatusByPatientId[patientId] = "succeeded";
        state.examFetchErrorByPatientId[patientId] = null;
      })
      .addCase(fetchNephrologyExam.rejected, (state, action) => {
        const patientId = String(action.meta.arg || "");
        state.examFetchStatusByPatientId[patientId] = "failed";
        state.examFetchErrorByPatientId[patientId] = action.payload || action.error?.message;
      })
      .addCase(saveNephrologyExam.pending, (state, action) => {
        const patientId = String(action.meta.arg?.patientId || "");
        state.examSaveStatusByPatientId[patientId] = "saving";
        state.examSaveErrorByPatientId[patientId] = null;
      })
      .addCase(saveNephrologyExam.fulfilled, (state, action) => {
        const { patientId, record } = action.payload;
        const records = state.examByPatientId[patientId] || [];
        const index = records.findIndex((item) => item.id === record?.id);
        if (index < 0) records.unshift(record);
        else records[index] = record;
        state.examByPatientId[patientId] = records;
        state.examSaveStatusByPatientId[patientId] = "succeeded";
        state.examSaveErrorByPatientId[patientId] = null;
      })
      .addCase(saveNephrologyExam.rejected, (state, action) => {
        const patientId = String(action.meta.arg?.patientId || "");
        state.examSaveStatusByPatientId[patientId] = "failed";
        state.examSaveErrorByPatientId[patientId] = action.payload || action.error?.message;
      })
      .addCase(fetchNephrologyUrinaryAndUremia.pending, (state, action) => {
        const patientId = String(action.meta.arg || "");
        state.urinaryAndUremiaFetchStatusByPatientId[patientId] = "loading";
        state.urinaryAndUremiaFetchErrorByPatientId[patientId] = null;
      })
      .addCase(fetchNephrologyUrinaryAndUremia.fulfilled, (state, action) => {
        const { patientId, records } = action.payload;
        state.urinaryAndUremiaByPatientId[patientId] = records;
        state.urinaryAndUremiaFetchStatusByPatientId[patientId] = "succeeded";
        state.urinaryAndUremiaFetchErrorByPatientId[patientId] = null;
      })
      .addCase(fetchNephrologyUrinaryAndUremia.rejected, (state, action) => {
        const patientId = String(action.meta.arg || "");
        state.urinaryAndUremiaFetchStatusByPatientId[patientId] = "failed";
        state.urinaryAndUremiaFetchErrorByPatientId[patientId] = action.payload || action.error?.message;
      });
  },
});

export const { clearNephrologyHistory } = nephrologySlice.actions;
export default nephrologySlice.reducer;